import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { Observable, tap } from 'rxjs';
import { Dao } from '../dao';
import { DataLoader } from '../data-loader';
import { DataLoaderAuthentified } from '../data-loader-authentified';

export interface BudgetAggregation {
  accountId: number;
  userId: number;
  amount: number;
  monthlyAmount: number;
  automatic: boolean;
  budgetType: 'EXPENSE' | 'EARNING';
  category: string;
  type: string;
  frequency: 'MENS' | 'ANNU';
  id?: number;
  startDate?: string;
  endDate?: string;
  label?: string;
  source?: string;
  sourceId?: number;
}

export interface CreateBudget {
  id?: number;
  accountId: number;
  automatic?: boolean;
  budgetType: 'EXPENSE' | 'EARNING';
  frequency: 'MENS' | 'ANNU';
  category: string;
  type: string;
  label?: string;
  amount: number;
}

export interface BudgetTypeRef {
  id: string;
  label: string;
  automatic?: boolean;
  category: {
    id: string;
    value: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class BudgetDao extends Dao {
  private earningTypes: DataLoaderAuthentified<BudgetTypeRef[]> =
    new DataLoaderAuthentified<BudgetTypeRef[]>(this.getAuthentified(), user => {
      return this.httpApi.get(`/ref/budget/types/earning`);
    });
  private expenseTypes: DataLoader<BudgetTypeRef[]> = new DataLoader<BudgetTypeRef[]>(() =>
    this.httpApi.get(`/ref/budget/types/expense`),
  );

  private budgetsExpense: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentified(), user => {
      return this.httpApi.get(
        `/accounts/${user.accountId}/budgets?budgetType=EXPENSE`,
      );
    });

  private budgetsEarning: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentified(), user => {
      return this.httpApi.get(
        `/accounts/${user.accountId}/budgets?budgetType=EARNING`,
      );
    });

  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  /* Expense */
  getBudgetsExpense(): Observable<BudgetAggregation[]> {
    return this.budgetsExpense.get();
  }

  /* Earning */
  getBudgetsEarning(): Observable<BudgetAggregation[]> {
    return this.budgetsEarning.get();
  }

  /* Ref Types */
  getEarningTypes(): Observable<BudgetTypeRef[]> {
    return this.earningTypes.get();
  }

  getExpenseTypes(): Observable<BudgetTypeRef[]> {
    return this.expenseTypes.get();
  }

  saveBudget(
    budgetItems: CreateBudget[],
    reset: boolean = true,
  ): Observable<CreateBudget> {
    if (!budgetItems || budgetItems.length === 0) {
      throw new Error('budgetItems cannot be empty');
    }
    if (!budgetItems[0].accountId) {
      throw new Error('accountId is required');
    }
    return this.httpApi
      .post(`/accounts/${budgetItems[0].accountId}/budgets`, budgetItems)
      .pipe(
        tap(() => {
          if (reset) {
            this.budgetsExpense?.reset();
            this.budgetsEarning?.reset();
          }
        }),
      );
  }

  deleteBudget(entity: CreateBudget): Observable<any> {
    return this.httpApi
      .delete(`/accounts/${entity.accountId}/budgets/${entity.id}`)
      .pipe(
        tap(() => {
          this.budgetsExpense?.reset();
          this.budgetsEarning?.reset();
        }),
      );
  }

  /* state */
  reset() {
    this.budgetsExpense?.reset();
    this.budgetsEarning?.reset();
  }

  onUserChange(event: any) {
    this?.reset();
  }
}
